/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/tslint/config */
import { Enumeration } from './enum.class';
import { ValidationBase } from './validation-base.class';
import { Backend, LogicFnRef, PrimitiveType, Storage } from './workflow.type';
import { ErrCallback, FlatData } from '../class/storage-backend.base';
import { statefulArrayHelper } from './stateful-value.helper';
import { dependencies } from './workflow.dependency';

export const PATH_ITERATION_SYMBOL = '$';
export const PATH_PAGE_ITERATION_SYMBOL = '#';
export const FIELD_ITERATION_SYMBOL = '*';

const SIGNED_FLOAT_RX = new RegExp('[^\\-0-9\\.]', 'g');
const START_SIGN_RX = new RegExp('(?!^)-', 'g');

/**
 * Front-end for value state
 */
export class StatefulValue extends ValidationBase {
  /**
   * StatefulValue store
   */
  private static readonly instances: { [id: string]: StatefulValue } = {};

  /**
   * Storage unit specific config
   * TODO: make this readonly again once the config is properly loaded
   * and checkconfig is removed
   */
  public config: Storage;

  /**
   * Protocol of storageId (defines storage backend)
   */
  public readonly protocol = this.id.split(':')[0];

  /**
   * Indicates if this has been loaded at least once in the past
   */
  public isLoaded = false;

  /**
   * Backend reference
   */
  protected readonly backend = dependencies.storageService.backends[this.protocol];

  /**
   * onChange() subscriptions
   */
  private readonly onChangeCallbacks: (() => void)[] = [];

  /**
   * updateCallback() subscriptions
   */
  private readonly afterPatchCallbacks: (() => void)[] = [];

  /**
   * Current value (if different than `.saved`)
   */
  private current: PrimitiveType;

  /**
   * Bump this number up for locking (i.e on focusing a field sourcing this value) or down to unlock (on blur)
   */
  private _saveLock = false;

  /**
   * Saved (aka original) value
   */
  private saved: PrimitiveType;

  /**
   * Explicit request to extend array on the back-end.
   * This should be the only way for the UI to trigger array extension (to avoid race conditions)
   * NOTE: addition on the backend may be asyncronous. Don't expect the array to be extended right after calling this method
   * Use the callback, if needed
   * @param configPath array root path or full path (must include protocol)
   */
  public static arrayAdd(configPath: string, cb?: ErrCallback): void;
  public static arrayAdd(configPath: string, appendData: FlatData, cb?: ErrCallback): void;
  public static arrayAdd(configPath: string, arg1: FlatData | ErrCallback, arg2?: ErrCallback): void {
    const path = StatefulValue.arrayInfo(configPath);
    const info = dependencies.storageInfoService.parse(configPath);
    const processed = info && info.pageIterationId != null ? path.replace(PATH_PAGE_ITERATION_SYMBOL, String(info.pageIterationId)) : path;
    if (processed.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error('Page iteration ID required');
    }
    // The arg2 callback was never actually implemented correctly, so we are going to
    // update the code to always call the callback after the arrayAdd completes
    if (typeof arg2 === 'function') {
      dependencies.storageService.arrayAdd(processed, <FlatData>arg1, () => {
        arg2(); // Ensure the original callback is called after arrayAdd completes
      });
    } else if (typeof arg1 === 'function') {
      // Pass a wrapper callback to arrayAdd
      dependencies.storageService.arrayAdd(processed, () => {
        arg1(); // Ensure the original callback is called after arrayAdd completes
      });
    } else {
      dependencies.storageService.arrayAdd(processed, arg1, arg2);
    }
  }

  /**
   * Explicit request to extend array
   * This should be the only way for the UI to trigger array shrinking (to avoid race conditions)
   * NOTE: addition on the backend may be asyncronous. Don't expect the array to be changed right after calling this method.
   * Use the callback, if needed
   * @param configPath including iterartion ID (not $)
   */
  public static arrayRemove(configPath: string, index?: number, cb?: ErrCallback): void {
    const path = StatefulValue.arrayInfo(configPath);

    // Implementing callbacks correctly, if one is provided.
    if (typeof cb === 'function') {
      // Pass a wrapper callback to arrayRemove
      dependencies.storageService.arrayRemove(path, index, () => {
        cb(); // Ensure the original callback is called after arrayRemove completes
      });
    } else {
      // If no callback is provided, just call arrayRemove
      dependencies.storageService.arrayRemove(path, index);
    }
  }

  /**
   * Tell the length of the array based on the '$' in the stateful value ID (aka path)
   * @param configPath stateful value ID (should include the '$' or '#')
   * @param pageIterationId required when page is iterated (pathes with #)
   * @param throwOnMissingArray throws error if array is missing (defaults to returning 0)
   */
  public static arraySize(configPath: string, pageIterationId?: number, throwOnMissingArray?: boolean): number {
    return statefulArrayHelper.arraySize(configPath, pageIterationId, throwOnMissingArray);
  }

  /**
   * Explicit request to empty an array
   * This should be the only way for the UI to trigger array shrinking (to avoid race conditions)
   * NOTE: addition on the backend may be asynchronous. Don't expect the array to be changed right after calling this method.
   * Use the callback, if needed
   * @param storageId including iteration ID (not $)
   */
  public static arrayTruncate(storageId: string, cb?: ErrCallback): void {
    const path = StatefulValue.arrayInfo(storageId);

    // Implementing callbacks correctly, if one is provided.
    if (typeof cb === 'function') {
      // Pass a wrapper callback to arrayTruncate
      dependencies.storageService.arrayTruncate(path, () => {
        cb(); // Ensure the original callback is called after arrayTruncate completes
      });
    } else {
      // If no callback is provided, just call arrayTruncate
      dependencies.storageService.arrayTruncate(path);
    }
  }

  /**
   * Explicit request to empty an array
   * This should be the only way for the UI to trigger array shrinking (to avoid race conditions)
   * NOTE: addition on the backend may be asynchronous. Don't expect the array to be changed right after calling this method.
   * Use the callback, if needed.
   * NOTE 2: This does not get array info - use with caution, and assert your path beforehand.
   * @param storageId including iteration ID (not $)
   */
  public static arrayTruncateWithPageIteration(storageId: string, cb?: ErrCallback): void {
    // const path = StatefulValue.arrayInfo(storageId);

    // Implementing callbacks correctly, if one is provided.
    if (typeof cb === 'function') {
      // Pass a wrapper callback to arrayTruncate
      dependencies.storageService.arrayTruncate(storageId, () => {
        cb(); // Ensure the original callback is called after arrayTruncate completes
      });
    } else {
      // If no callback is provided, just call arrayTruncate
      dependencies.storageService.arrayTruncate(storageId);
    }
  }

  /**
   * Check if array root is being adjusted on backend
   * (equals to backend.isLoading for now - it covers both init, and any load/save that can change arrays)
   * @param configPath including iteration ID (not $)
   */
  public static isArrayAdjusting(configPath: string): boolean {
    const backend = dependencies.storageService.backends[configPath.split(':')[0]];
    return backend && backend.isLoading;
  }

  /**
   * Values are updated (or added) on bundle data receival
   * @param data propagated dictionary of changes
   */
  public static onBundleUpdate(data: FlatData): void {
    for (const [storageId, value] of Object.entries(data)) {
      const valueConfig = dependencies.storageInfoService.parse(storageId);
      if (valueConfig) {
        new StatefulValue(valueConfig.storageId, valueConfig.iterationId, valueConfig.pageIterationId).setSavedValue(value);
      }
    }
  }

  /**
   * Below method should be called while switching the bundle to refresh all storage ids
   * Make sure this method called only once
   */
  public static reloadData(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [id, instance] of Object.entries(StatefulValue.instances)) {
      instance.revert();
    }
  }

  /**
   * Below method should be called to clear statefulValue
   * Make sure this method called only once
   */
  public static clear(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [id, instance] of Object.entries(StatefulValue.instances)) {
      delete StatefulValue.instances[id];
    }
    dependencies.stateManagementService.clear();
  }

  /**
   * Safely convert values, such as "-$123,323.32" to number types (-123323.32)
   * @param value any primitive type
   */
  public static numericValue(value: PrimitiveType): number {
    if (typeof value === 'number' || value == null) {
      return <number>value;
    }
    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    }
    // tslint:disable-next-line: no-magic-numbers
    const str = String(value).replace(SIGNED_FLOAT_RX, '').replace(START_SIGN_RX, '').split('.').slice(0, 2).join('.');
    return str ? +str : null;
  }

  /**
   * Test configuration values for validity + set sane defaults (e.g. empty object where missing, adding implicit IDs, etc)
   * Throws errors on integration fails
   * @param id card ID from `workflow-cards.json`
   */
  public static test(backendType: Backend, id: string): void {
    const config = dependencies.config.storage[backendType][id];
    StatefulValue.ensureConfig(config, id);
  }

  private static ensureConfig(config: Storage, id: string): void {
    // ensure consistency with IDs
    config.id = id;

    // ensure existing type
    config.type = config.type || 'string';

    // ensure existing validation
    config.validation = config.validation || {};
  }

  /**
   * URL info on path. Will redirect for arrayLink
   * @param configPath path to examine
   */
  private static arrayInfo(configPath: string): string {
    return statefulArrayHelper.arrayInfo(configPath);
  }

  /**
   * Unique stateful value
   * @param id static ID including the storage backend (ex: 'api:path/x/y')
   * @param iterationId optional dynamic part for the id (replacement for the `$` sign)
   * @param pageIterationId optional page loop id (replacement for the `#` sign)
   */
  constructor(public readonly id: string, public iterationId?: number, public pageIterationId?: number) {
    super();

    if (!this.backend) {
      throw new Error(`No backend found for protocol "${this.protocol}"`);
    }

    if (iterationId != null) {
      if (id.indexOf(PATH_ITERATION_SYMBOL) < 0) {
        this.iterationId = null;
      } else {
        this.id = id.replace(PATH_ITERATION_SYMBOL, String(iterationId));
      }
    } else if (id.indexOf(PATH_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array path requires iteration ID: ${id}`);
    }
    if (pageIterationId != null) {
      if (this.id.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0) {
        this.pageIterationId = null;
      } else {
        this.id = this.id.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
      }
    } else if (this.id.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array path requires page iteration ID: ${id}`);
    }

    if (StatefulValue.instances[this.id]) {
      return StatefulValue.instances[this.id];
    }
    StatefulValue.instances[this.id] = this;

    if (iterationId != null && this.backend.type === 'api' && StatefulValue.arraySize(id, this.pageIterationId) <= iterationId) {
      throw new Error(
        `Can not implicitly create array instance: ${id}@${iterationId}, array size:` +
          ` ${StatefulValue.arraySize(id, this.pageIterationId)}`
      );
    }
    const info = dependencies.storageInfoService.parse(id);
    this.config = info && info.config;
    if (!this.config) {
      if (this.backend.type === 'api') {
        throw new Error(`Could not find storage info config for ${id}`);
      }
      this.config = { id, type: 'string', validation: {} };
    } else {
      StatefulValue.ensureConfig(this.config, id);
    }
  }

  /**
   * Temporary fix to resolve this.config being null
   * https://dev.azure.com/cudirect/Origence/_workitems/edit/263108
   */
  public checkConfig(config) {
    let attempts = 0;
    const maxAttempts = 1000;
    while (!config) {
      const info = dependencies.storageInfoService.parse(this.id);
      if (attempts >= maxAttempts) {
        console.log('Failed to resolve config after 1000 attempts on ID: ' + this.id);
        this.config = { id: this.id, type: 'string', validation: {} };
        break;
      }
      // Attempt to populate this.config
      config = info?.config;
      attempts++;
    }
    this.config = config;
  }

  /**
   * Value is modified, but not saved yet
   */
  public get isChanged(): boolean {
    return typeof this.current !== 'undefined';
  }

  /**
   * Backend has started an async operation regarding this field, and is not finished yet
   */
  public get isLoading(): boolean {
    return this.backend.isLoading;
  }

  /**
   * Saved (aka original) value
   */
  public get savedValue(): PrimitiveType {
    return this.saved;
  }

  /**
   * convert config.validation object to LogicFnRef array
   */
  public get validationLogicFn(): LogicFnRef[] {
    this.checkConfig(this.config);
    if (!this.config?.validation) {
      return [];
    }

    return Object.keys(this.config.validation).map((validationFn) => {
      const arg: PrimitiveType[] = [];
      if (Array.isArray(this.config.validation[validationFn])) {
        this.config.validation[validationFn].forEach((a: PrimitiveType) => arg.push(dependencies.stringVariableService.parse(a)));
      } else {
        arg.push(dependencies.stringVariableService.parse(this.config.validation[validationFn]));
      }
      return <LogicFnRef>[validationFn, ...arg];
    });
  }

  /**
   * Current value
   */
  public get value(): PrimitiveType {
    this.checkConfig(this.config);

    if (!this.config) {
      console.log(this.id + ' has no type');
    }

    const value = this.isChanged ? this.current : this.saved;
    const type = this.config && this.config?.type;
    return value == null ? value : type === 'boolean' ? !!value : type === 'number' ? StatefulValue.numericValue(value) : String(value);
  }

  /**
   * Current value as number (or null or undefined)
   */
  public get valueNumber(): number {
    return StatefulValue.numericValue(this.value);
  }

  /**
   * Current value as number (null or undefined will be 0)
   */
  public get valueNumberOnly(): number {
    const value = this.valueNumber;
    return value == null ? 0 : value;
  }

  /**
   * Current value as string (or null or undefined)
   */
  public get valueString(): string {
    const value = this.value;
    return value == null ? <null>value : String(value);
  }

  /**
   * Current value as string (null and undefined become empty string)
   */
  public get valueStringOnly(): string {
    const value = this.value;
    return value == null ? '' : String(value);
  }

  /**
   * Impose new current value over saved value (or revert if they are identical)
   */
  // tslint:disable-next-line: adjacent-overload-signatures
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set value(value: PrimitiveType) {
    this.checkConfig(this.config);

    if (!this.config) {
      console.log(this.id + ' has no type');
    }
    const sanitizedValue =
      value == null
        ? value
        : this.config?.type === 'boolean'
        ? !!value
        : this.config?.type === 'number'
        ? StatefulValue.numericValue(value)
        : String(value);
    if (sanitizedValue !== this.current) {
      const changed = this.value !== sanitizedValue;
      if (sanitizedValue === this.saved) {
        this.revert();
      } else {
        this.current = sanitizedValue;
      }

      if (changed) {
        for (const callback of this.onChangeCallbacks) {
          callback();
        }
      }

      this.save();
    }
  }

  /**
   * @inheritdoc
   */
  public hasValidation(fnName: string): PrimitiveType[] {
    return dependencies.logicService.hasFunction(fnName, this.validationLogicFn);
  }

  /**
   * Trigger loading from backend + mark loaded value as saved/original
   */
  public load(callback?: () => void): void {
    dependencies.storageService.load(this.id, (err, value) => {
      this.isLoaded = true;
      if (!err) {
        this.setSavedValue(value);
      }
      if (callback) {
        callback();
      }
      // TODO: add error propagation
    });
  }

  /**
   * Callbacks for value change
   * @param callback fn to call on value change
   */
  public onChange(callback: () => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * Callbacks: after value patched
   * @param callback fn to call after value updated
   */
  public afterPatch(callback: () => void): void {
    this.afterPatchCallbacks.push(callback);
  }

  /**
   * Trigger revalidation
   */
  public revalidate(): string[] {
    return this.spreadValidation(
      dependencies.logicService.evaluate(this.validationLogicFn, { value: this }, 'collect', this.iterationId, this.pageIterationId)
    );
  }

  /**
   * Roll back change in value
   */
  public revert(): void {
    delete this.current;
  }

  /**
   * Trigger saving to backend + merge current value to original if successful
   */
  public save(): Promise<boolean> {
    if (
      !this.valid ||
      (this.value != null && this.config.enum && !new Enumeration(this.config.enum, true).values.find((i) => i.value === this.value))
    ) {
      return Promise.resolve(false);
    }
    if (this.isChanged && !this._saveLock) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return new Promise<boolean>((resolve, reject) => {
        dependencies.storageService.save(this.id, this.value, (err) => {
          if (!err) {
            this.saved = this.value;
            dependencies.stateManagementService.appendValue(this.id, this.saved);
            delete this.current;
            resolve(true);
          }
          resolve(false);
          // TODO: add error propagation
        });
      });
    }
  }

  /**
   * Below method should be called to clear statefulValue
   * Make sure this method called only once
   */
  public static saveChangedValues(): Promise<boolean[]> {
    const promises: Promise<boolean>[] = [];
    for (const [id, instance] of Object.entries(StatefulValue.instances)) {
      if (instance.isChanged) {
        const promise = StatefulValue.instances[id].save();
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        if (promise) promises.push(promise);
      }
    }
    return Promise.all(promises);
  }

  /**
   * Bump up _saveLock counter
   */
  public saveLock(): void {
    this._saveLock = true;
  }

  /**
   * Knock down _saveLock counter. Check if saving is due
   * @param noSave prevents saving on reaching 0-lock
   */
  public saveUnlock(noSave?: boolean): void {
    this._saveLock = false;
    if (!this._saveLock && !noSave) {
      this.save();
    }
  }

  /**
   * Unsubscribe on change callbacks
   * @param callback function ref for onChange callback
   */
  public unsubscribe(callback: () => void): void {
    let pos = this.onChangeCallbacks.indexOf(callback);
    if (pos > -1) {
      this.onChangeCallbacks.splice(pos, 1);
    }

    pos = this.afterPatchCallbacks.indexOf(callback);
    if (pos > -1) {
      this.afterPatchCallbacks.splice(pos, 1);
    }
  }

  /**
   * Set the saved (and current) value, propagates change (if any)
   * @param value new value to be treated as saved
   */
  protected setSavedValue(value: PrimitiveType): void {
    this.saved = value;
    if (this.current === value) {
      delete this.current;
    }
    for (const callback of this.afterPatchCallbacks) {
      callback();
    }
  }
}
